'use client'

import { Moon, Sun } from 'lucide-react'
import { useTheme } from '@/hooks/use-theme'
import { THEME_CHANGE_EVENT } from '@/lib/dither-colors'
import { systemTheme, THEME_OVERRIDE_KEY } from '@/lib/theme'

/**
 * Light/dark theme switch. The site follows the visitor's system setting
 * (THEME_BOOTSTRAP in lib/theme, run pre-paint by the root layout); this
 * overrides it for the rest of the tab session and broadcasts to the WebGL
 * dither effects so they re-paint live. Switching back to the system's
 * theme drops the override, so the site follows the system again.
 * The knob position is driven by html[data-theme] in CSS, so it animates
 * even when the theme is changed elsewhere. Labels come in as props so the
 * component stays a presentational client leaf inside the server footer.
 */
export function ThemeToggle({
  className,
  labelToDark,
  labelToLight,
}: {
  className?: string
  labelToDark: string
  labelToLight: string
}) {
  const theme = useTheme() ?? 'light'

  function toggle() {
    const next = theme === 'dark' ? 'light' : 'dark'
    document.documentElement.dataset.theme = next
    try {
      if (next === systemTheme()) sessionStorage.removeItem(THEME_OVERRIDE_KEY)
      else sessionStorage.setItem(THEME_OVERRIDE_KEY, next)
    } catch {}
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: next }))
  }

  const dark = theme === 'dark'
  return (
    <button
      type="button"
      role="switch"
      aria-checked={dark}
      onClick={toggle}
      className={className}
      aria-label={dark ? labelToLight : labelToDark}
    >
      <span className="home-theme-toggle-knob" aria-hidden>
        {dark ? <Moon strokeWidth={2.2} /> : <Sun strokeWidth={2.2} />}
      </span>
    </button>
  )
}
